/**
 * The wire shapes the API actually sends (PLAN §14).
 *
 * These mirror the presenters in `apps/api/src/routes/`, not the database
 * rows: dates arrive as ISO strings and per-target caption text arrives
 * already resolved, so the UI never re-implements server logic.
 */
import type {
  AccountKind,
  ConnectionStatus,
  MediaKind,
  MediaStatus,
  Platform,
  PostStatus,
  PostType,
  TargetStatus,
} from '@dintiko/shared';

export interface SessionUser {
  id: string;
  email: string;
  name: string | null;
  timezone: string;
}

/** A restriction or caution the server attached to an account. */
export interface AccountNote {
  code: string;
  level: 'info' | 'warn';
  message: string;
}

/**
 * Exactly what `GET /api/accounts` sends. Kept separate from the shape the
 * screens use so the mapping lives in one place (see data/accounts.ts) rather
 * than every component learning the server's field names.
 */
export interface AccountWire {
  id: string;
  platform: Platform;
  kind: AccountKind;
  handle: string;
  displayName: string | null;
  avatarUrl: string | null;
  isEnabled: boolean;
  simulates: Platform | null;
  defaultSettings: Record<string, unknown>;
  createdAt: string;
  connection: {
    id: string;
    status: ConnectionStatus;
    expiresAt: string | null;
    message: string | null;
    detail: string | null;
  };
  notes: AccountNote[];
}

export interface Account {
  id: string;
  platform: Platform;
  kind: AccountKind;
  /** Which real platform a mock account stands in for (PLAN §9.6). */
  simulates: Platform | null;
  handle: string;
  displayName: string | null;
  avatarUrl: string | null;
  enabled: boolean;
  connectionStatus: ConnectionStatus;
  /** When the login expires, for the "expiring on 14 Dec" note. */
  expiresAt: string | null;
  /** Why the login is unhappy, in the server's own words. */
  connectionMessage: string | null;
  /** "TikTok: posts are private until the audit passes." */
  notes: AccountNote[];
  defaultSettings: Record<string, unknown>;
}

export function toAccount(wire: AccountWire): Account {
  return {
    id: wire.id,
    platform: wire.platform,
    kind: wire.kind,
    simulates: wire.simulates,
    handle: wire.handle,
    displayName: wire.displayName,
    avatarUrl: wire.avatarUrl,
    enabled: wire.isEnabled,
    connectionStatus: wire.connection.status,
    expiresAt: wire.connection.expiresAt,
    connectionMessage: wire.connection.message,
    notes: wire.notes,
    defaultSettings: wire.defaultSettings,
  };
}

/** The one note worth showing on a crowded row: a warning beats a remark. */
export function headlineNote(account: Account): string | null {
  const warning = account.notes.find((note) => note.level === 'warn');
  return (warning ?? account.notes[0])?.message ?? null;
}

/** The platform whose rules actually apply — mock accounts borrow one. */
export function effectivePlatform(account: Account): Platform {
  return account.simulates ?? account.platform;
}

/** True when the account cannot be posted to right now, with the reason. */
export function accountUnavailable(account: Account): string | null {
  if (!account.enabled) return 'Disabled in Accounts.';
  if (account.connectionStatus === 'needs_reconnect' || account.connectionStatus === 'revoked') {
    return account.connectionMessage ?? `${account.handle} needs reconnecting.`;
  }
  return null;
}

export interface AccountGroup {
  id: string;
  name: string;
  accountIds: string[];
}

/** Exactly what `GET /api/media` sends. */
export interface MediaWire {
  id: string;
  kind: MediaKind;
  status: MediaStatus;
  originalFilename: string | null;
  mime: string | null;
  sizeBytes: number;
  durationMs: number | null;
  width: number | null;
  height: number | null;
  storageTier: string;
  hasThumbnail: boolean;
  createdAt: string;
  readyAt: string | null;
  usageCount: number;
  orientation: 'portrait' | 'landscape' | 'square' | null;
  folder: string | null;
}

export interface MediaAsset {
  id: string;
  kind: MediaKind;
  status: MediaStatus;
  originalFilename: string | null;
  sizeBytes: number;
  durationMs: number | null;
  width: number | null;
  height: number | null;
  createdAt: string;
  usageCount: number;
  orientation: 'portrait' | 'landscape' | 'square' | null;
  /** Which folder it is filed under. Null is the unfiled bucket. */
  folder: string | null;
  /** Null when no thumbnail was produced, so the card shows a placeholder. */
  thumbnailUrl: string | null;
}

/** One folder, as the library lists them. Null is the unfiled bucket. */
export interface MediaFolder {
  name: string | null;
  count: number;
}

export function toMediaAsset(wire: MediaWire): MediaAsset {
  return {
    id: wire.id,
    kind: wire.kind,
    status: wire.status,
    originalFilename: wire.originalFilename,
    sizeBytes: wire.sizeBytes,
    durationMs: wire.durationMs,
    width: wire.width,
    height: wire.height,
    createdAt: wire.createdAt,
    usageCount: wire.usageCount,
    orientation: wire.orientation,
    folder: wire.folder,
    // Served by the API behind the session, so it is a path, not a public URL.
    thumbnailUrl: wire.hasThumbnail ? `/api/media/${wire.id}/thumbnail` : null,
  };
}

/** Portrait decides which platforms treat it as a Short, Reel or normal video. */
export function isVertical(media: Pick<MediaAsset, 'orientation'>): boolean {
  return media.orientation === 'portrait';
}

/**
 * A Tailwind aspect class matching what the file actually is.
 *
 * Every thumbnail was hardcoded to 9/16, so a 1920×1080 video rendered as a
 * tall centre-cropped sliver — which is misleading twice over, because
 * orientation is exactly the thing that decides whether a platform treats this
 * as a Short, a Reel, or a plain video.
 */
export function aspectClass(media: Pick<MediaAsset, 'orientation'>): string {
  if (media.orientation === 'landscape') return 'aspect-video';
  if (media.orientation === 'square') return 'aspect-square';
  return 'aspect-[9/16]';
}

export interface PostParts {
  hook: string;
  body: string | null;
  cta: string | null;
  link: string | null;
  hashtags: string[];
}

export interface Post extends PostParts {
  id: string;
  type: PostType;
  status: PostStatus;
  scheduledAt: string | null;
  staggerMinutes: number;
  createdAt: string;
  updatedAt: string;
  confirmedAt: string | null;
  completedAt: string | null;
}

export interface PostTarget {
  id: string;
  accountId: string;
  platform: Platform | null;
  handle: string | null;
  status: TargetStatus;
  scheduledAt: string | null;
  title: string | null;
  text: string;
  /** The owner has edited this tab's text away from the generated version. */
  edited: boolean;
  /** Edited, and then a caption part changed underneath it (PLAN §11.4). */
  staleOverride: boolean;
  settings: Record<string, unknown>;
  externalUrl: string | null;
  /**
   * The publisher's own code for the last failure.
   *
   * The message alone cannot drive a control: two Telegram failures read
   * differently and need different recoveries, and only one of them means
   * "we cannot tell whether this posted".
   */
  lastErrorCode: string | null;
  lastErrorMessage: string | null;
}

/**
 * What a post points at, which is not the same as the asset itself.
 *
 * The post only stores references; the facts — filename, thumbnail,
 * orientation — come from the media library, which the composer already
 * loads. This was typed as `MediaAsset[]` and the API has never sent that,
 * so `media.map((a) => a.id)` quietly produced `[undefined]` and every post
 * opened looking like it had no video attached.
 */
export interface PostMediaRef {
  assetId: string;
  variantId: string | null;
  position: number;
}

export interface PostDetail {
  post: Post;
  targets: PostTarget[];
  media: PostMediaRef[];
}

export interface TargetEvent {
  id: string;
  level: 'info' | 'warn' | 'error';
  event: string;
  message: string;
  data: Record<string, unknown> | null;
  /** ISO-8601 UTC. The API sends `at`; it was read here as `createdAt`. */
  at: string;
}

/** One target's answer from preflight/confirm (PLAN §9.7). */
export interface PlanTarget {
  targetId: string;
  accountId: string;
  platform: Platform;
  handle: string;
  title: string | null;
  text: string;
  scheduledAt: string;
  errors: PlanIssue[];
  warnings: PlanIssue[];
}

/**
 * One thing wrong, as the preflight reports it.
 *
 * The API has always sent these as objects; the type here claimed `string[]`
 * and the confirm sheet rendered each one directly, which React refuses —
 * the sheet crashed the moment a post had anything to say about it.
 */
export interface PlanIssue {
  /** Stable code, so the composer can focus the right field. */
  code: string;
  /** Plain language, says what to do next (PLAN §13.1). */
  message: string;
  field?: string | undefined;
}

export interface ConfirmPlan {
  publishable: boolean;
  scheduledAt: string;
  errors: PlanIssue[];
  warnings: PlanIssue[];
  targets: PlanTarget[];
}

/**
 * Exactly what `GET /api/settings` sends.
 *
 * The per-platform signature is deliberately not here — it lives on each
 * platform's caption rule, because LinkedIn and TikTok want different ones
 * (PLAN §11.2).
 */
export interface Settings {
  timezone: string;
  defaultStaggerMinutes: number;
  archiveAfterDays: number;
  /** "18:30", used to prefill the schedule picker. Null means no preference. */
  defaultScheduleTime: string | null;
  notify: Record<string, unknown>;
  ai: Record<string, unknown>;
  updatedAt: string;
}

export interface HashtagSet {
  id: string;
  name: string;
  tags: string[];
}

/**
 * One platform's caption rules, editable in Settings (PLAN §11.2).
 *
 * Re-exported rather than redefined: the browser builds its live previews with
 * the same `buildCaption` the server runs at confirm time, so a second shape
 * here would be a second source of truth and the preview would eventually lie.
 */
export type { CaptionRule } from '@dintiko/shared';

/**
 * Target statuses that mean work is in flight, so the screen should keep
 * polling (PLAN §13.4).
 */
export const LIVE_TARGET_STATUSES: readonly TargetStatus[] = [
  'queued',
  'running',
  'waiting',
] as const;

export function hasLiveTargets(targets: readonly { status: TargetStatus }[]): boolean {
  return targets.some((target) => LIVE_TARGET_STATUSES.includes(target.status));
}
